import InstagramIcon from "@/assets/colorable/instagram.svg?react";
import TiktokIcon from "@/assets/colorable/tiktok.svg?react";
import UgcIcon from "@/assets/colorable/ugc.svg?react";
import SocialProfileLink from "@/components/Common/Influencer/SocialProfileLink";
import LazyTooltip from "@/components/Common/LazyTooltip";
import {useIsAdmin} from "@/hooks/useUserHooks";
import clsxm from "@/lib/clsxm";
import type {TSidebarInfluencer} from "@/store/influencerSidebarContext";
import type {TCampaignType} from "@/store/types";
import {sortBy} from "lodash";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

const InfluencerSocials: FC<{
  className?: string;
  containerClassName?: string;
  iconClassName?: string;
  influencer: TSidebarInfluencer;
  selectedPlatform?: TCampaignType;
  showUsername?: boolean;
}> = ({
  className,
  containerClassName,
  iconClassName,
  influencer,
  selectedPlatform = "instagram",
  showUsername = true,
}) => {
  const {t} = useTranslation(["common", "influencer_list"]);
  const isAdmin = useIsAdmin();

  const socials = [
    {
      platform: "instagram" as const,
      icon: InstagramIcon,
      verified: influencer.user.is_verified || (isAdmin && !!influencer?.user.username),
      url: "https://www.instagram.com/",
      username: influencer.user.username,
    },
    {
      platform: "tiktok" as const,
      icon: TiktokIcon,
      verified: influencer.user.tiktok_verified || (isAdmin && !!influencer?.user.tiktok_username),
      url: "https://www.tiktok.com/@",
      username: influencer.user.tiktok_username,
    },
    {
      platform: "ugc" as const,
      icon: UgcIcon,
      verified: influencer.user.ugc_verified,
    },
  ];

  const sortedSocials = sortBy(socials, s => (s.platform === selectedPlatform ? 0 : 1)).filter(s => s.verified);

  return (
    <div className={clsxm("flex min-w-0 items-start gap-1", containerClassName)}>
      {sortedSocials.length === 0 ? (
        <span className="border-neutral-white-dark text-neutral-black text-2xs flex h-6 items-center rounded border px-2">
          {t("influencer_list:unverified")}
        </span>
      ) : (
        sortedSocials.map(social => {
          const isSelected = selectedPlatform === social.platform;

          return (
            <div
              key={social.platform}
              className={clsxm(
                "border-neutral-white-dark flex h-6 items-center justify-center gap-2 rounded border",
                isSelected && showUsername && social.platform !== "ugc" ? "min-w-0 overflow-hidden px-2" : "w-6",
                className,
              )}
              onClick={event => {
                if (social.url && social.username) {
                  event.stopPropagation();
                  window.open(`${social.url}${social.username}`, "_blank");
                }
              }}>
              <social.icon
                data-tooltip-id={`influencer-${influencer.uuid}-${social.platform}-verification`}
                className={clsxm("text-neutral-black h-auto w-2.5 shrink-0 outline-none", iconClassName)}
              />
              <LazyTooltip
                className="z-1"
                id={`influencer-${influencer.uuid}-${social.platform}-verification`}
                variant="dark"
                place="bottom">
                {t(`${social.platform as "instagram"}Verified`)}
              </LazyTooltip>
              {isSelected && showUsername && social.platform !== "ugc" && (
                <SocialProfileLink
                  className="hover:text-primary-medium-solid text-2xs truncate overflow-hidden leading-none whitespace-nowrap"
                  platform={social.platform}
                  showLogo={false}
                  username={social.username}
                />
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default InfluencerSocials;
